import React, { useState, useMemo } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { RateWithDetails } from '../../../../../api/types/lecturers/user-details-lecturer';
import { RequestRateStatus } from '../../../../../api/types/lecturers/price-rate/request-rate/request-rate-status';
import dateUtils from '../../../../../utils/date-format';
import RateDetailsDialog from '../../../../home/components/rates/rate-details-dialog';
import { LecturerRateDisplayType } from '../../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';

interface RatesStatusesProps {
  rates: RateWithDetails[];
  onRefresh?: () => void;
}

const statusLabels: Record<RequestRateStatus, string> = {
  [RequestRateStatus.Pending]: 'ממתין לאישור',
  [RequestRateStatus.Approved]: 'אושר',
  [RequestRateStatus.Denied]: 'נדחה',
};

const statusSeverity: Record<
  RequestRateStatus,
  'success' | 'info' | 'warning' | 'danger'
> = {
  [RequestRateStatus.Pending]: 'warning',
  [RequestRateStatus.Approved]: 'success',
  [RequestRateStatus.Denied]: 'danger',
};

export const RatesStatuses: React.FC<RatesStatusesProps> = ({
  rates,
  onRefresh,
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [branchFilter, setBranchFilter] = useState<string>('');
  const [selectedRate, setSelectedRate] = useState<RateWithDetails | null>(
    null,
  );
  const [dialogVisible, setDialogVisible] = useState(false);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = rates.length;
    const pending = rates.filter(
      (r) => r.requestStatus === RequestRateStatus.Pending,
    ).length;
    const approved = rates.filter(
      (r) => r.requestStatus === RequestRateStatus.Approved,
    ).length;
    const denied = rates.filter(
      (r) => r.requestStatus === RequestRateStatus.Denied,
    ).length;

    return {
      total,
      pending,
      approved,
      denied,
      totalValue: rates.reduce((sum, r) => sum + r.rate, 0),
    };
  }, [rates]);

  // Filter rates based on search and filters
  const filteredRates = useMemo(() => {
    return rates.filter((rate) => {
      const matchesStatus =
        !statusFilter || rate.requestStatus === statusFilter;
      const matchesBranch = !branchFilter || rate.branchName === branchFilter;
      const matchesSearch =
        !globalFilter ||
        `${rate.firstName} ${rate.lastName}`
          .toLowerCase()
          .includes(globalFilter.toLowerCase()) ||
        rate.nationalId?.includes(globalFilter) ||
        rate.branchName?.toLowerCase().includes(globalFilter.toLowerCase()) ||
        rate.activityName?.toLowerCase().includes(globalFilter.toLowerCase());

      return matchesStatus && matchesBranch && matchesSearch;
    });
  }, [rates, statusFilter, branchFilter, globalFilter]);

  // Get unique branches and statuses for filters
  const branches = Array.from(new Set(rates.map((r) => r.branchName))).map(
    (branch) => ({
      label: branch,
      value: branch,
    }),
  );

  const statuses = Object.entries(statusLabels).map(([value, label]) => ({
    label,
    value,
  }));

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Handle row click
  const handleRowClick = (rate: RateWithDetails) => {
    setSelectedRate(rate);
    setDialogVisible(true);
  };

  const handleDialogHide = () => {
    setDialogVisible(false);
    setSelectedRate(null);
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      await onRefresh();
    }
    setDialogVisible(false);
  };

  const header = (
    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
      <h2 className="text-2xl font-bold m-0">
        <i className="pi pi-chart-line ml-2"></i>
        סטטוס תעריפי מרצים
      </h2>
      <div className="flex gap-2">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="חיפוש כללי..."
          />
        </span>
        <Dropdown
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.value)}
          options={[{ label: 'כל הסטטוסים', value: '' }, ...statuses]}
          placeholder="סנן לפי סטטוס"
          showClear
        />
        <Dropdown
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.value)}
          options={[{ label: 'כל השלוחות', value: '' }, ...branches]}
          placeholder="סנן לפי שלוחה"
          showClear
        />
      </div>
    </div>
  );

  return (
    <div className="rates-statuses-dashboard" dir="rtl">
      <style>{`
        .rates-table-clickable .p-datatable-tbody > tr {
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .rates-table-clickable .p-datatable-tbody > tr:hover {
          background-color: #f8fafc !important;
        }
      `}</style>

      {/* Statistics Overview */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div style={{ flex: '1', minWidth: '0' }}>
          <Card className="text-center">
            <div className="flex align-items-center justify-content-center">
              <div>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#e3f2fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <i className="pi pi-list text-xl text-blue-500"></i>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {stats.total}
                </div>
                <div className="text-secondary">סך הכל תעריפים</div>
              </div>
            </div>
          </Card>
        </div>

        <div style={{ flex: '1', minWidth: '0' }}>
          <Card className="text-center">
            <div className="flex align-items-center justify-content-center">
              <div>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#fff3e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <i className="pi pi-clock text-xl text-orange-500"></i>
                </div>
                <div className="text-2xl font-bold text-orange-600">
                  {stats.pending}
                </div>
                <div className="text-secondary">ממתין לאישור</div>
              </div>
            </div>
          </Card>
        </div>

        <div style={{ flex: '1', minWidth: '0' }}>
          <Card className="text-center">
            <div className="flex align-items-center justify-content-center">
              <div>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#e8f5e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <i className="pi pi-check-circle text-xl text-green-500"></i>
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {stats.approved}
                </div>
                <div className="text-secondary">אושרו</div>
              </div>
            </div>
          </Card>
        </div>

        <div style={{ flex: '1', minWidth: '0' }}>
          <Card className="text-center">
            <div className="flex align-items-center justify-content-center">
              <div>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#ffebee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <i className="pi pi-times-circle text-xl text-red-500"></i>
                </div>
                <div className="text-2xl font-bold text-red-600">
                  {stats.denied}
                </div>
                <div className="text-secondary">נדחו</div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Divider />

      {/* Rates Table */}
      <DataTable
        value={filteredRates}
        /*header={header}*/
        emptyMessage={
          <div style={{ textAlign: 'right' }}>אין תעריפים להצגה</div>
        }
        scrollHeight="500px"
        scrollable
        dataKey="id"
        tableStyle={{
          direction: 'rtl',
        }}
        onRowClick={(e) => handleRowClick(e.data as RateWithDetails)}
        rowClassName={() => 'p-datatable-row-clickable'}
        className="rates-table-clickable"
      >
        <Column
          field="fullName"
          header="שם מרצה"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <div style={{ fontSize: '13px' }}>
              {`${rowData.firstName} ${rowData.lastName}`}
            </div>
          )}
        />

        <Column
          field="nationalId"
          header="ת.ז"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <div style={{ fontSize: '13px' }}>
              {rowData.nationalId || 'לא צוין'}
            </div>
          )}
        />

        <Column
          field="branchName"
          header="שלוחה"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <div style={{ fontSize: '13px' }}>{rowData.branchName}</div>
          )}
        />

        <Column
          field="activityName"
          header="סוג פעילות"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <div style={{ fontSize: '13px' }}>{rowData.activityName}</div>
          )}
        />

        <Column
          field="rate"
          header="תעריף"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <div style={{ fontSize: '13px', fontWeight: '500' }}>
              {formatCurrency(rowData.rate)}
            </div>
          )}
        />

        <Column
          field="displayType"
          header="סוג תצוגה"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <Tag
              value={
                rowData.displayType === LecturerRateDisplayType.OneHour
                  ? 'שעתי'
                  : 'אקדמי'
              }
              severity={
                rowData.displayType === 'HOURLY_PRICE' ? 'info' : 'success'
              }
            />
          )}
        />

        <Column
          field="requestStatus"
          header="סטטוס"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <Tag
              value={statusLabels[rowData.requestStatus]}
              severity={statusSeverity[rowData.requestStatus]}
            />
          )}
        />

        <Column
          field="createdAt"
          header="תאריך יצירה"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <div style={{ fontSize: '13px' }}>
              {dateUtils.displayFormatDate(rowData.createdAt)}
            </div>
          )}
        />

        <Column
          field="createdByName"
          header="נוצר על ידי"
          style={{ textAlign: 'right', fontSize: '14px' }}
          headerStyle={{
            textAlign: 'right',
            fontSize: '14px',
            fontWeight: 'bold',
          }}
          body={(rowData: RateWithDetails) => (
            <div style={{ fontSize: '13px' }}>
              {rowData.createdByName || 'לא ידוע'}
            </div>
          )}
        />
      </DataTable>

      {/* Rate Details Dialog */}
      <RateDetailsDialog
        visible={dialogVisible}
        onHide={handleDialogHide}
        rateWithDetails={selectedRate}
        onRefresh={handleRefresh}
      />
    </div>
  );
};
